import { create } from "zustand";
import { toast } from "react-toastify";
import axiosInstance from "../../../../Hooks/axoisInstance";
import { API_BASE_URL, API_BASE_URL3 } from "../../../Services/baseApiUrl";

const getFileNameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;
  const match = disposition.match(
    /filename\*?=(?:UTF-8''|")?([^";]+)"?/i
  );
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1].replace(/['"]/g, "").trim());
  } catch {
    return match[1].replace(/['"]/g, "").trim() || fallback;
  }
};

const normalizeIncorrectData = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.incorrectData)) return data.incorrectData;
  if (Array.isArray(data?.errors?.incorrectData)) return data.errors.incorrectData;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.errors?.data)) return data.errors.data;
  return [];
};

const getHeaderErrors = (data) => {
  if (Array.isArray(data?.errors?.header)) {
    return data.errors.header.filter(Boolean).map(String);
  }
  if (Array.isArray(data?.header)) {
    return data.header.filter(Boolean).map(String);
  }
  if (Array.isArray(data?.errors?.missingColumns)) {
    return data.errors.missingColumns.filter(Boolean).map(String);
  }
  if (Array.isArray(data?.missingColumns)) {
    return data.missingColumns.filter(Boolean).map(String);
  }
  return [];
};

const getMessageText = (data) => {
  if (typeof data?.message === "string") return data.message;
  if (typeof data === "string") return data;
  return "";
};

const looksLikeHeaderOrColumnError = (message = "") => {
  const lower = String(message).toLowerCase();
  if (!lower) return false;
  return (
    lower.includes("missing column") ||
    lower.includes("missing columns") ||
    lower.includes("invalid header") ||
    lower.includes("invalid headers") ||
    lower.includes("required column") ||
    lower.includes("required columns") ||
    lower.includes("column is missing") ||
    lower.includes("columns are missing") ||
    (lower.includes("header") && lower.includes("missing"))
  );
};

const getUploadErrorMessage = (data, fallback = "Upload failed") => {
  const message =
    (typeof data?.message === "string" && data.message) ||
    (typeof data === "string" && data) ||
    fallback;
  const headerErrors = getHeaderErrors(data);

  if (headerErrors.length > 0) {
    return `${message} ${headerErrors.join(" ")}`.trim();
  }

  return message;
};

const buildHeaderErrorRows = (headerErrors = []) =>
  headerErrors.map((msg, index) => ({
    rowNumber: index + 1,
    remarks: msg,
    status: "Incorrect",
    errors: { General: [msg] },
  }));

const mergeIncorrectWithHeaderErrors = (incorrectData = [], headerErrors = []) => {
  if (!headerErrors.length) return incorrectData;
  if (!incorrectData.length) return buildHeaderErrorRows(headerErrors);

  const existingRemarks = new Set(
    incorrectData
      .map((row) => String(row?.remarks || "").trim().toLowerCase())
      .filter(Boolean)
  );

  const headerRows = headerErrors
    .filter((msg) => !existingRemarks.has(String(msg).trim().toLowerCase()))
    .map((msg, index) => ({
      rowNumber: `H${index + 1}`,
      remarks: msg,
      status: "Incorrect",
      errors: { General: [msg] },
    }));

  return [...headerRows, ...incorrectData];
};

const resolveIncorrectData = (responseData) => {
  let incorrectData = normalizeIncorrectData(responseData);
  let headerErrors = getHeaderErrors(responseData);
  const messageText = getMessageText(responseData);

  if (
    headerErrors.length === 0 &&
    looksLikeHeaderOrColumnError(messageText)
  ) {
    headerErrors = [messageText];
  }

  // Column-missing rows often only have remarks / errors.General
  if (headerErrors.length === 0) {
    incorrectData.forEach((row) => {
      const remark = String(row?.remarks || "").trim();
      const generalErrors = row?.errors?.General;
      const generalMsg = Array.isArray(generalErrors)
        ? String(generalErrors[0] || "").trim()
        : "";
      const candidate = remark || generalMsg;
      if (candidate && looksLikeHeaderOrColumnError(candidate)) {
        headerErrors.push(candidate);
      }
    });
  }

  return mergeIncorrectWithHeaderErrors(incorrectData, headerErrors);
};

const fetchIncorrectData = async () => {
  try {
    const incorrectResponse = await axiosInstance.get(
      `${API_BASE_URL}incorrect`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return resolveIncorrectData(incorrectResponse?.data);
  } catch (error) {
    // /incorrect may return 4xx with errors.incorrectData in the body
    return resolveIncorrectData(error?.response?.data);
  }
};

export const useRfidBulkUploadStore = create((set) => ({
  isBulkUploadLoading: false,
  isSaving: false,
  isSampleDownloading: false,
  showBulkUploadModal: false,
  bulkUploadStep: "form",
  incorrectData: [],

  openBulkUploadModal: () => {
    set({
      showBulkUploadModal: true,
      bulkUploadStep: "form",
      incorrectData: [],
      isSaving: false,
    });
  },

  downloadRfidSampleFile: async () => {
    set({ isSampleDownloading: true });
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL3}MasterDetails/GetRFIDSampleFile`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const fileName = getFileNameFromDisposition(
        response.headers?.["content-disposition"],
        "RFIDSampleFile.xlsx"
      );

      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to download sample file"
      );
      return { success: false };
    } finally {
      set({ isSampleDownloading: false });
    }
  },

  bulkUploadRfid: async (file) => {
    set({ isBulkUploadLoading: true });
    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await axiosInstance.post(
        `${API_BASE_URL}upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (uploadResponse.status !== 200) {
        toast.error("Upload failed");
        set({ isBulkUploadLoading: false });
        return { success: false };
      }

      // Prefer incorrectData from /upload (includes missing-column + row errors)
      let incorrectData = resolveIncorrectData(uploadResponse?.data);

      // Fall back to /incorrect when upload body has no error rows
      if (incorrectData.length === 0) {
        incorrectData = await fetchIncorrectData();
      }

      set({
        incorrectData,
        showBulkUploadModal: true,
        bulkUploadStep: "result",
        isBulkUploadLoading: false,
      });

      if (incorrectData.length > 0) {
        toast.error(
          "Please correct the below errors and re-upload the Excel file."
        );
      } else {
        toast.success(
          uploadResponse?.data?.message || "RFID file uploaded successfully"
        );
      }

      return { success: true };
    } catch (error) {
      const responseData = error?.response?.data;
      let incorrectData = resolveIncorrectData(responseData);

      // Prefer /incorrect row data when upload fails (same as Employee flow)
      if (incorrectData.length === 0) {
        try {
          incorrectData = await fetchIncorrectData();
        } catch {
          // ignore secondary incorrect fetch failure
        }
      }

      // Missing columns and/or row validation errors → Check Uploaded File modal
      if (incorrectData.length > 0) {
        set({
          incorrectData,
          showBulkUploadModal: true,
          bulkUploadStep: "result",
          isBulkUploadLoading: false,
        });
        toast.error(
          getUploadErrorMessage(
            responseData,
            "Please correct the below errors and re-upload the Excel file."
          )
        );
        return { success: false };
      }

      toast.error(getUploadErrorMessage(responseData));
      set({ isBulkUploadLoading: false });
      return { success: false };
    }
  },

  saveRfidBulkUpload: async () => {
    set({ isSaving: true });
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}SaveRFIDBulkUpload`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success(response?.data?.message || "Saved successfully");
        set({
          showBulkUploadModal: false,
          bulkUploadStep: "form",
          incorrectData: [],
          isSaving: false,
        });
        return { success: true };
      }

      toast.error("Something went wrong");
      set({ isSaving: false });
      return { success: false };
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Save failed"
      );
      set({ isSaving: false });
      return { success: false };
    }
  },

  closeBulkUploadModal: () => {
    set({
      showBulkUploadModal: false,
      bulkUploadStep: "form",
      incorrectData: [],
      isSaving: false,
    });
  },
}));
